import { type FC, useMemo, useState } from "react";
import { parseColor } from "react-stately";

import { CardSeparator } from "../Card/CardSeparator.tsx";
import { DialogComponent } from "../Dialog/Dialog.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { ColorArea } from "./ColorArea.tsx";
import { colorPalettes } from "./ColorPalettes.ts";
import { ColorSelect } from "./ColorSelect.tsx";
import { ColorSlider } from "./ColorSlider.tsx";
import { HexColorField } from "./HexColorField.tsx";
import { COLOR_PICKER_DEFAULT_COLOR } from "./settings.ts";

export interface ColorPickerProps {
  defaultColor?: string;
  closeFn: () => void;
  onApply?: (color: string) => void;
}

export const ColorPicker: FC<ColorPickerProps> = (props) => {
  const [color, setColor] = useState(parseColor(props.defaultColor ?? COLOR_PICKER_DEFAULT_COLOR).toFormat("hsb"));

  // [H, S, B]
  const [xChannel, yChannel, zChannel] = useMemo(() => color.getColorChannels(), [color]);

  return (
    <DialogComponent
      data-testid="color-picker"
      width={199}
      style={{ minWidth: 199 }}
      title=" "
      actions={[
        <HexColorField
          key="hex-color-field"
          value={color}
          onChange={(newColor) => {
            if (newColor) {
              setColor(newColor?.toFormat("hsb"));
            }
          }}
          aria-label="color-picker-input"
        />,
      ]}
      mainButton={{
        label: "Apply",
        icon: "colorize",
        onPress: () => {
          props.onApply?.(color.toString("hex"));
          props.closeFn();
        },
      }}
      closeFn={props.closeFn}
    >
      <VStack>
        <ColorArea
          size={[179, 85]}
          value={color}
          onChange={setColor}
          xChannel={xChannel}
          yChannel={yChannel}
          style={{ marginBottom: 8 }}
        />
        <ColorSlider channel={zChannel} value={color} onChange={setColor} />
        <CardSeparator style={{ margin: "5px 0" }} />
        <HStack>
          {Object.entries(colorPalettes).map(([paletteName, palette]) => (
            <VStack key={paletteName} spacing={0} style={{ borderRadius: 5, overflow: "hidden", height: 72 }}>
              {Object.values(palette).map((color, index) => {
                return (
                  <ColorSelect
                    key={`color-select-${index}`}
                    color={parseColor(color as string).toString("hsb")}
                    onPressSetColor={setColor}
                  />
                );
              })}
            </VStack>
          ))}
        </HStack>
      </VStack>
    </DialogComponent>
  );
};
